// Shared table for navigator manage pages (athletes, coaches, groups, etc.)

import React, {
  useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import { useTable, useSortBy } from 'react-table';
import { useSticky } from 'react-table-sticky';
import { FixedSizeList, VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import PropTypes from 'prop-types';
import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';

import Avatar from '../../../../../shared/_TBComponents/Avatar/Avatar';
import Button from '../../../../../shared/_TBComponents/Button';
import Icon from '../../../../../shared/_TBComponents/Icon';
import Input from '../../../../../shared/_TBComponents/Input';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import Text from '../../../../../shared/_TBComponents/Text';
import Tooltip from '../../../../../shared/_TBComponents/Tooltip';

const ROW_HEIGHT = 56;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 16px 16px 16px;
  box-sizing: border-box;
  border-bottom: 1px solid #D6D6D6;
`;

const ExtraActionSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 1 auto;
`;

const InlineBannerSlot = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media (max-width: 1000px) {
    order: -1;
    flex-basis: 100%;
  }
`;

const SearchRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    flex-basis: 100%;
  }
`;

const BannerWrapper = styled.div`
  padding: 0 16px;
  box-sizing: border-box;
`;

// Host for a supplied search control (e.g. a composed row of type dropdown +
// search + tag filter), which owns its own field styling. Sized to content —
// growing would push the bulk bar to the far edge instead of keeping it right
// after the controls like the plain-search tables.
const SearchControlSlot = styled.div`
  flex: 0 1 auto;
  min-width: 250px;
`;

// Exported so composed searchControl rows can reuse the standard field look.
export const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 250px;
  display: flex;
  align-items: center;

  input {
    border: 1px solid #D6D6D6;
    border-radius: 4px;
    font-family: "Nunito Sans";

    &::placeholder {
      color: #9E9E9E;
      font-weight: 400;
    }
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 45%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  display: flex;
  align-items: center;
`;

const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const TableContainer = styled.div`
  font-family: "Nunito Sans";
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px 16px 0 16px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);

  ${(props) => ((props.virtualizeRows || props.isLoading) && `
    flex: 1;
    min-height: 0;
  `)}
`;

const HScroller = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: visible;

  ${(props) => (props.virtualizeRows && `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  `)}

  ${(props) => (!props.virtualizeRows && props.isLoading && `
    flex: 1;
    min-height: 0;
  `)}
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  background: #F2F2F2;
  border-bottom: 1px solid #D6D6D6;
  position: sticky;
  top: 0;
  z-index: 4;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 12px 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: #1A1A1A;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: ${(props) => (props.canSort ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background: ${(props) => (props.canSort ? '#F0F0F0' : 'transparent')};
  }
`;

const VirtualBody = styled.div`
  flex-shrink: 0;
`;

// Virtualized body must fill the remaining height so react-window's AutoSizer
// can measure a definite height to window against.
const VirtualBodyFill = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

// Highlight pulse logic when deep linking from manage buttons
const mergeHighlightFade = keyframes`
  from { background-color: #fff3e0; box-shadow: inset 0 0 0 1px #ff6600; }
  to { background-color: transparent; box-shadow: inset 0 0 0 1px transparent; }
`;

const mergeHighlightFadeSticky = keyframes`
  from {
    background-color: #fff3e0;
    box-shadow: inset 0 1px 0 0 #ff6600, inset -1px 0 0 0 #ff6600, inset 0 -1px 0 0 #ff6600;
  }
  to {
    background-color: transparent;
    box-shadow: inset 0 1px 0 0 transparent, inset -1px 0 0 0 transparent,
      inset 0 -1px 0 0 transparent;
  }
`;

const VirtualRow = styled('div', {
  shouldForwardProp: (prop) => !['autoHeight', 'isSelected', 'isClickable', 'onMeasure'].includes(prop),
})`
  display: flex;
  align-items: stretch;
  height: ${(props) => (props.autoHeight ? 'auto' : `${ROW_HEIGHT}px`)};
  ${(props) => props.autoHeight && `min-height: ${ROW_HEIGHT}px;`}
  border-bottom: 1px solid #D6D6D6;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  transition: background 0.1s ease;
  box-sizing: border-box;
  [data-sticky-cell] { background: #FFFFFF; }
  ${(props) => props.isSelected && `
    background: #F7F9FF;
    [data-sticky-cell] { background: #F7F9FF; }
  `}
  &.is-highlighted {
    animation: ${mergeHighlightFade} 2.4s ease-out;
    [data-sticky-cell] { animation: ${mergeHighlightFadeSticky} 2.4s ease-out; }
  }
`;

const VirtualCell = styled.div`
  display: flex;
  align-items: center;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const SpinnerContainer = styled.div`
  flex: 1;
  min-height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyStateContainer = styled.div`
  flex: 1;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const BulkDropdownContainer = styled.div`
  position: relative;
`;

const BulkDropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-left: 8px;
  background: ${(props) => props.bgColor || '#1A1A1A'};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.hoverColor || '#333333'};
  }
`;

const BulkDropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 225px;
  z-index: 10;
  overflow: hidden;
`;

const BulkDropdownDivider = styled.div`
  height: 1px;
  background: #EEEEEE;
  margin: 0 12px;
`;

const BulkDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  font-family: "Nunito Sans";
  font-size: 16px;
  line-height: 1;
  color: #1A1A1A;
  background: none;
  border: none;
  cursor: pointer;

  svg {
    display: block;
    flex-shrink: 0;
    margin-bottom: 2px;
  }

  &:hover {
    background: #F7F7F7;
  }
`;

const CheckboxInput = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 2px solid #929292;
  border-radius: 3px;
  outline: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${(props) => (props.variant === 'minus' ? `
    &:checked, &:indeterminate {
      border-color: #77A7FF;
    }

    &:checked::after, &:indeterminate::after {
      content: '';
      position: absolute;
      top: 6px;
      left: 3px;
      width: 8px;
      height: 2px;
      background: #77A7FF;
      display: block;
    }
  ` : `
    &:checked {
      border-color: #77A7FF;
    }

    &:checked::after {
      content: '';
      position: absolute;
      top: 1px;
      left: 5px;
      width: 5px;
      height: 10px;
      border: solid #77A7FF;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      display: block;
    }
  `)}
`;

export const SquareCheckbox = ({
  checked, indeterminate, disabled, onChange, variant,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <CheckboxInput
      ref={ref}
      type='checkbox'
      variant={variant}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

SquareCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['check', 'minus']),
};

SquareCheckbox.defaultProps = {
  indeterminate: false,
  disabled: false,
  variant: 'check',
};

// Shared Cell Components (exported for use in specific table files)

export const AvatarCell = ({ row }) => {
  const { pic, first, last } = row.original;
  return <Avatar avatar={pic} name={`${first} ${last}`} size={36} />;
};

AvatarCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      pic: PropTypes.string,
      first: PropTypes.string,
      last: PropTypes.string,
    }),
  }).isRequired,
};

const OverflowSpan = styled.span`
  display: block;
  overflow: hidden;
  width: 100%;
  word-break: break-word;
`;

export const OverflowCell = ({ value }) => (
  <OverflowSpan>
    {value || <span style={{ color: '#444444' }}>-</span>}
  </OverflowSpan>
);

OverflowCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

OverflowCell.defaultProps = {
  value: '',
};

const AvatarNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
`;

export const AvatarNameCell = ({ row, value }) => {
  const { pic, first, last } = row.original;
  return (
    <AvatarNameWrapper>
      <Avatar avatar={pic} name={`${first} ${last}`} size={36} />
      <OverflowSpan>
        {value || <span style={{ color: '#444444' }}>-</span>}
      </OverflowSpan>
    </AvatarNameWrapper>
  );
};

AvatarNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      pic: PropTypes.string,
      first: PropTypes.string,
      last: PropTypes.string,
    }),
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AvatarNameCell.defaultProps = {
  value: '',
};

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

const AdaptiveListWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => JUSTIFY[props.align] || 'center'};
  gap: 6px;
  flex-wrap: ${(props) => (props.maxLines === 1 ? 'nowrap' : 'wrap')};
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

export const Pill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'singleLine',
})`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #EEEEEE;
  border-radius: 2px;
  padding: 0 6px;
  color: ${(props) => props.textColor || '#616161'};
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;

  ${(props) => props.singleLine && `
    flex: 0 1 auto;
    min-width: 0;
  `}
`;

const PillText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'singleLine',
})`
  display: block;

  ${(props) => props.singleLine && `
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const PillButton = styled(Pill)`
  border: none;
  background: #EAF2FF;
  color: #1787FB;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const ItemTagSpacer = styled.span`
  margin-left: 6px;
`;

const OverflowTooltipList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: left;
  line-height: 1.5;
  font-size: 13px;
  font-weight: 400;
`;

const OverflowTooltipTag = styled.span`
  margin-left: 4px;
  color: #9E9E9E;
`;

// Tag color dot shown inside a Pill when the item carries a color. The border
// keeps light colors (the default is white) visible against the grey chip.
const PillDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid #D6D6D6;
  background: ${(props) => props.color};
`;

const PlusMoreBadge = styled.span`
  display: inline-block;
  flex-shrink: 0;
  border-radius: 2px;
  padding: 0 6px;
  text-align: center;
  font-family: "Nunito Sans";
  line-height: 150%;
  white-space: nowrap;
  color: ${(props) => props.color};
  font-size: ${(props) => props.size};
  font-weight: ${(props) => props.weight};
`;

// Per-variant look. `text` joins names into one comma-separated run with a blue
// badge (groups, calendars); `pill` renders each name as a grey chip with a grey
// badge (tags, programs). Any of these can be overridden per column via props.
const VARIANT_DEFAULTS = {
  text: {
    align: 'center', badgeColor: '#77A7FF', badgeSize: '14px', badgeWeight: '700', emptyText: '-', emptyColor: '#444444',
  },
  pill: {
    align: 'left', badgeColor: '#77A7FF', badgeSize: '14px', badgeWeight: '700', emptyText: '–', emptyColor: '#CCCCCC',
  },
};

// Renders a list of named items with a "+ N more" overflow badge. With
// adaptiveWrap on it shows up to `maxVisible` items, then drops one at a time if
// the row would spill past `maxLines` lines — keeps cells from ballooning
// vertically. Shared across every navigator manage table so the badge and its
// overflow behavior stay identical; `variant` picks plain-text vs pill rendering.
const MAX_WRAP_LINES = 2;

export const OverflowNameList = ({
  items, idPrefix, rowId, variant, align, maxVisible, adaptiveWrap,
  maxLines, badgeColor, badgeSize, badgeWeight, emptyText, emptyColor,
}) => {
  const assignments = items || [];
  const containerRef = useRef(null);
  const observedWidthRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(Math.min(maxVisible, assignments.length));

  useLayoutEffect(() => {
    setVisibleCount(Math.min(maxVisible, assignments.length));
  }, [assignments, maxVisible]);

  useLayoutEffect(() => {
    if (!adaptiveWrap) return;
    const el = containerRef.current;
    if (!el || visibleCount < 2) return;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 21;
    const containerStyle = getComputedStyle(el);
    let textMeasureContext;
    const getNaturalTextWidth = (innerEl) => {
      const innerStyle = getComputedStyle(innerEl);
      const { scrollWidth, clientWidth } = innerEl;
      if (scrollWidth > clientWidth) return scrollWidth;

      const canvas = document.createElement('canvas');
      textMeasureContext = textMeasureContext || canvas.getContext('2d');
      if (!textMeasureContext) return Math.max(scrollWidth, clientWidth);
      textMeasureContext.font = innerStyle.font;
      const measuredWidth = textMeasureContext.measureText(innerEl.textContent || '').width;
      if (scrollWidth === 0 || measuredWidth > clientWidth) return measuredWidth;
      return Math.max(scrollWidth, clientWidth);
    };
    const isOverflowing = maxLines === 1
      ? Array.from(el.children).reduce((total, child) => {
        if (child.dataset.badge === 'true') {
          return total + child.getBoundingClientRect().width;
        }

        const pillStyle = getComputedStyle(child);
        const pillChildren = Array.from(child.children);
        const naturalWidth = pillChildren.reduce((width, childNode) => {
          const innerEl = childNode.matches('[data-pill-text]')
            ? childNode
            : null;
          const childWidth = innerEl
            ? getNaturalTextWidth(innerEl)
            : Math.max(childNode.scrollWidth, childNode.getBoundingClientRect().width);
          return width + childWidth;
        }, 0)
          + parseFloat(pillStyle.paddingLeft)
          + parseFloat(pillStyle.paddingRight)
          + (pillChildren.length > 1
            ? (parseFloat(pillStyle.columnGap || pillStyle.gap) || 5) * (pillChildren.length - 1)
            : 0);
        return total + naturalWidth;
      }, 0) + ((el.children.length - 1)
        * (parseFloat(containerStyle.columnGap || containerStyle.gap) || 6))
        > el.clientWidth + 1
      : el.scrollHeight > (lineHeight * maxLines) + 1;
    if (isOverflowing) {
      setVisibleCount((count) => count - 1);
    }
  }, [visibleCount, assignments, adaptiveWrap, maxLines]);

  useLayoutEffect(() => {
    if (!adaptiveWrap) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;

    observedWidthRef.current = null;
    const handleResize = (entries) => {
      const width = entries?.[0]?.contentRect?.width || el.clientWidth;
      const widthChanged = observedWidthRef.current !== null
        && observedWidthRef.current !== width;
      observedWidthRef.current = width;
      if (widthChanged) setVisibleCount(Math.min(maxVisible, assignments.length));
    };

    if (typeof window.ResizeObserver === 'function') {
      const observer = new window.ResizeObserver(handleResize);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adaptiveWrap, assignments, maxVisible]);

  useLayoutEffect(() => {
    if (!adaptiveWrap || !document.fonts || document.fonts.status === 'loaded') return undefined;
    let mounted = true;

    const resetAfterFontsLoad = async () => {
      await document.fonts.ready;
      if (mounted) setVisibleCount(Math.min(maxVisible, assignments.length));
    };

    resetAfterFontsLoad();
    return () => {
      mounted = false;
    };
  }, [adaptiveWrap, assignments, maxVisible]);

  const defaults = VARIANT_DEFAULTS[variant] || VARIANT_DEFAULTS.text;

  if (assignments.length === 0) {
    const color = emptyColor || defaults.emptyColor;
    return <Text styledTextProps={{ color }}>{emptyText || defaults.emptyText}</Text>;
  }

  const shown = assignments.slice(0, visibleCount);
  const extra = assignments.length - visibleCount;
  const hiddenItems = assignments.slice(visibleCount);
  const tooltipItems = hiddenItems.slice(0, 12);
  const remainingHiddenItems = hiddenItems.length - tooltipItems.length;
  const badge = {
    color: badgeColor || defaults.badgeColor,
    size: badgeSize || defaults.badgeSize,
    weight: badgeWeight || defaults.badgeWeight,
  };

  const renderPill = (item) => {
    const PillComponent = item.onClick ? PillButton : Pill;
    return (
      <PillComponent
        key={item.id}
        data-pill='true'
        type={item.onClick ? 'button' : undefined}
        onClick={item.onClick}
        singleLine={maxLines === 1}
        title={maxLines === 1 ? item.name : item.title}
      >
        {item.color && <PillDot color={item.color} />}
        {maxLines === 1 ? <PillText data-pill-text='true' singleLine>{item.name}</PillText> : item.name}
      </PillComponent>
    );
  };

  return (
    <AdaptiveListWrapper ref={containerRef} align={align || defaults.align} maxLines={maxLines}>
      {variant === 'pill'
        ? shown.map((i) => (
          <React.Fragment key={i.id}>
            {renderPill(i)}
            {i.tag && (
              <Pill data-pill='true' singleLine={maxLines === 1} textColor={i.tagColor}>
                {maxLines === 1 ? <PillText data-pill-text='true' singleLine>{i.tag}</PillText> : i.tag}
              </Pill>
            )}
          </React.Fragment>
        ))
        : (
          <span>
            {shown.map((i, idx) => (
              <React.Fragment key={i.id}>
                {idx > 0 && ', '}
                {i.name}
                {i.tag && (
                  <ItemTagSpacer><Pill textColor={i.tagColor}>{i.tag}</Pill></ItemTagSpacer>
                )}
              </React.Fragment>
            ))}
          </span>
        )}
      {extra > 0 && (
        <PlusMoreBadge data-badge='true' color={badge.color} size={badge.size} weight={badge.weight}>
          <Tooltip
            id={`${idPrefix}-tooltip-${rowId}`}
            displayText={`+ ${extra} more`}
            hoverContent={(
              <OverflowTooltipList>
                {tooltipItems.map((item) => (
                  <li key={item.id}>
                    {item.name}
                    {item.tag && <OverflowTooltipTag>{item.tag}</OverflowTooltipTag>}
                  </li>
                ))}
                {remainingHiddenItems > 0 && (
                  <li><OverflowTooltipTag>{`and ${remainingHiddenItems} more`}</OverflowTooltipTag></li>
                )}
              </OverflowTooltipList>
            )}
            displaySize={badge.size}
            displayColor={badge.color}
            displayWeight={badge.weight}
            cursor='default'
            place='top'
            tooltipStyles={{
              zIndex: 5,
              maxWidth: 280,
              padding: '10px 12px',
              borderRadius: 6,
              textAlign: 'left',
              whiteSpace: 'normal',
            }}
          />
        </PlusMoreBadge>
      )}
    </AdaptiveListWrapper>
  );
};

OverflowNameList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    color: PropTypes.string,
    tag: PropTypes.string,
    tagColor: PropTypes.string,
    title: PropTypes.string,
    onClick: PropTypes.func,
  })),
  idPrefix: PropTypes.string.isRequired,
  rowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  variant: PropTypes.oneOf(['text', 'pill']),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  maxVisible: PropTypes.number,
  adaptiveWrap: PropTypes.bool,
  maxLines: PropTypes.number,
  badgeColor: PropTypes.string,
  badgeSize: PropTypes.string,
  badgeWeight: PropTypes.string,
  emptyText: PropTypes.string,
  emptyColor: PropTypes.string,
};

OverflowNameList.defaultProps = {
  items: [],
  rowId: undefined,
  variant: 'text',
  align: undefined,
  maxVisible: 2,
  adaptiveWrap: true,
  maxLines: MAX_WRAP_LINES,
  badgeColor: undefined,
  badgeSize: undefined,
  badgeWeight: undefined,
  emptyText: undefined,
  emptyColor: undefined,
};

export const GroupsCell = ({ row, column }) => (
  <OverflowNameList
    items={row.original.groupAssignments}
    idPrefix='groups'
    rowId={row.original.id}
    align={column?.cellStyle?.textAlign}
  />
);

GroupsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      groupAssignments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })),
    }),
  }).isRequired,
  column: PropTypes.shape({
    cellStyle: PropTypes.shape({ textAlign: PropTypes.string }),
  }),
};

GroupsCell.defaultProps = {
  column: {},
};

export const CalendarCell = ({ row, column }) => (
  <OverflowNameList
    items={row.original.calendarAssignments}
    idPrefix='calendar'
    rowId={row.original.id}
    align={column?.cellStyle?.textAlign}
  />
);

CalendarCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      calendarAssignments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
      })),
    }),
  }).isRequired,
  column: PropTypes.shape({
    cellStyle: PropTypes.shape({ textAlign: PropTypes.string }),
  }),
};

CalendarCell.defaultProps = {
  column: {},
};

const InvitedPill = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  border-radius: 2px;
  padding: 0 12px;
  color: #FFBE19;
  background: #FFCB471A;
  cursor: pointer;
`;

// Unified Status/Last Activity column shared by Athletes and Coaches to determine email resend
export const LastActivityCell = ({ row }) => {
  const registered = row.original.activated === 1;
  if (!registered) {
    return (
      <InvitedPill
        onClick={(e) => {
          e.stopPropagation();
          row.original.onResendActivation(row.original);
        }}
      >
        Invited
      </InvitedPill>
    );
  }
  return <OverflowCell value={row.original.lastActivity} />;
};

LastActivityCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      activated: PropTypes.number,
      lastActivity: PropTypes.string,
      onResendActivation: PropTypes.func,
    }),
  }).isRequired,
};

// Action Menu Hook exported for use in specific table files

export const useActionMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = (e) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const openUpward = window.innerHeight - rect.bottom < 190;
      setMenuStyle({
        position: 'fixed',
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target)
        && buttonRef.current && !buttonRef.current.contains(e.target)
      ) setIsOpen(false);
    };
    const closeOnScroll = () => setIsOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [isOpen]);

  return {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close: () => setIsOpen(false),
  };
};

export const ActionMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

export const ActionMenuButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  rotate: 90deg;
  color: #1A1A1A;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(119, 167, 255, 0.2);
    color: #77A7FF;
  }
`;

export const ActionMenuContainer = styled.div`
  background: #FFF;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px;
  z-index: 9999;
  min-width: 160px;
`;

export const ActionMenuDivider = styled.div`
  height: 1px;
  background: #D6D6D6;
  margin: 2px 8px;
`;

export const ActionMenuItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border-radius: 6px;
  font-family: "Nunito Sans";
  font-size: 16px;
  color: #1A1A1A;
  background: none;
  border: none;
  cursor: pointer;

  svg {
    display: block;
    flex-shrink: 0;
    margin-bottom: 2px;
  }

  &:hover {
    background: #F7F7F7;
  }
`;

// Edit/Delete row-action dropdown shared by every navigator manage table that
// only needs those two actions (tags, program tags, progressions) — built from
// the ActionMenu* primitives above so they stay visually identical.
export const SimpleActionsMenu = ({
  onEdit, onDelete, editLabel, deleteLabel, ariaLabel,
}) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();
  return (
    <ActionMenuWrapper onClick={(ev) => ev.stopPropagation()}>
      <ActionMenuButton
        ref={buttonRef}
        type='button'
        onClick={openMenu}
        aria-label={ariaLabel}
        aria-haspopup='menu'
        aria-expanded={isOpen}
      >
        <Icon icon='dots' size={16} color='#1A1A1A' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle} role='menu'>
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); onEdit(); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            {editLabel}
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' role='menuitem' onClick={() => { close(); onDelete(); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            {deleteLabel}
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

SimpleActionsMenu.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  editLabel: PropTypes.string,
  deleteLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
};

SimpleActionsMenu.defaultProps = {
  editLabel: 'Edit',
  deleteLabel: 'Delete',
  ariaLabel: 'Row actions',
};

// Internal: Sort Icon, Row Renderer, Selection Cells

const getSortIcon = (column) => {
  if (!column.isSorted) return 'Arrows-Sort';
  return column.isSortedDesc ? 'alt-down-arrow' : 'alt-up-arrow';
};

const SortIcon = ({ column }) => (
  <Icon
    icon={getSortIcon(column)}
    size={12}
    color={column.isSorted ? '#77A7FF' : '#DFDFDF'}
    styledProps={{ marginBottom: '2px' }}
  />
);

SortIcon.propTypes = {
  column: PropTypes.shape({ isSorted: PropTypes.bool, isSortedDesc: PropTypes.bool }).isRequired,
};

const TableRow = ({
  row, prepareRow, isScrolled, isHighlighted, style, virtualized, autoHeight, onMeasure,
}) => {
  const rowRef = useRef(null);
  prepareRow(row);

  useLayoutEffect(() => {
    if (!onMeasure || !rowRef.current) return undefined;
    const measure = () => {
      if (rowRef.current) {
        onMeasure(row.original.id, Math.ceil(rowRef.current.getBoundingClientRect().height));
      }
    };
    if (typeof ResizeObserver === 'undefined') {
      measure();
      return undefined;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(rowRef.current);
    measure();
    return () => observer.disconnect();
  }, [onMeasure, row.original.id]);

  return (
    <VirtualRow
      ref={rowRef}
      data-row-id={row.original.id}
      className={isHighlighted ? 'is-highlighted' : undefined}
      isSelected={row.original.isSelected}
      isClickable={!!row.original.onToggle}
      autoHeight={autoHeight}
      style={style}
      onClick={() => row.original.onToggle && row.original.onToggle(row.original.id)}
    >
      {row.cells.map((cell) => {
        const cs = cell.column.cellStyle || {};
        const hs = cell.column.headerStyle || {};
        const isSticky = !!cell.column.sticky;
        const cellProps = cell.getCellProps();
        return (
          <VirtualCell
            key={cell.column.id}
            data-column-id={cell.column.id}
            data-sticky-cell={isSticky || undefined}
            style={{
              ...cellProps.style,
              flex: cs.flex || hs.flex,
              width: (cs.flex || hs.flex) ? undefined : (cs.width || hs.width),
              minWidth: cs.minWidth || hs.minWidth,
              padding: cs.padding || '10px 16px',
              textAlign: cs.textAlign,
              overflow: cs.overflow || 'hidden',
              textOverflow: cs.textOverflow,
              whiteSpace: cs.whiteSpace,
              justifyContent: cs.textAlign === 'center' ? 'center' : undefined,
              ...(isSticky && {
                boxShadow: isScrolled ? '-1px 0 0 0 #EEEEEE' : 'none',
              }),
              ...(isSticky && virtualized && {
                position: 'relative',
                left: 'auto',
                right: 'auto',
                transform: 'translateX(var(--sticky-shift, 0px))',
                zIndex: 1,
              }),
            }}
          >
            {cell.render('Cell')}
          </VirtualCell>
        );
      })}
    </VirtualRow>
  );
};

TableRow.propTypes = {
  isScrolled: PropTypes.bool.isRequired,
  isHighlighted: PropTypes.bool,
  style: PropTypes.shape({}),
  virtualized: PropTypes.bool,
  autoHeight: PropTypes.bool,
  onMeasure: PropTypes.func,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      isSelected: PropTypes.bool,
      onToggle: PropTypes.func,
    }),
    cells: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  prepareRow: PropTypes.func.isRequired,
};

TableRow.defaultProps = {
  isHighlighted: false,
  style: undefined,
  virtualized: false,
  autoHeight: false,
  onMeasure: null,
};

// react-window row renderer (used only when virtualizeRows is on).
const VirtualRowRenderer = ({ index, style, data }) => {
  const {
    rows, prepareRow, isScrolled, highlightId, variableRows, onMeasure,
  } = data;
  const row = rows[index];
  if (variableRows) {
    return (
      <div style={style}>
        <TableRow
          row={row}
          prepareRow={prepareRow}
          isScrolled={isScrolled}
          isHighlighted={highlightId != null && row.original.id === highlightId}
          virtualized
          autoHeight
          onMeasure={onMeasure}
        />
      </div>
    );
  }
  return (
    <TableRow
      row={row}
      prepareRow={prepareRow}
      isScrolled={isScrolled}
      isHighlighted={highlightId != null && row.original.id === highlightId}
      style={style}
      virtualized
    />
  );
};

VirtualRowRenderer.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({
      original: PropTypes.shape({ id: PropTypes.number }),
    })).isRequired,
    prepareRow: PropTypes.func.isRequired,
    isScrolled: PropTypes.bool,
    highlightId: PropTypes.number,
    variableRows: PropTypes.bool,
    onMeasure: PropTypes.func,
  }).isRequired,
};

const SelectCell = ({ row }) => (
  <SquareCheckbox
    checked={row.original.isSelected}
    disabled={row.original.isSelectable === false}
    onChange={() => row.original.onToggle && row.original.onToggle(row.original.id)}
  />
);

SelectCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      isSelected: PropTypes.bool,
      isSelectable: PropTypes.bool,
      onToggle: PropTypes.func,
    }),
  }).isRequired,
};

export const nameSort = (rowA, rowB, columnId) => {
  const a = (rowA.values[columnId] || '').toLowerCase();
  const b = (rowB.values[columnId] || '').toLowerCase();
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

// Relevance tiers (same scheme as Build's exercise picker): exact match, then
// name starts with the query, then any word does, then substring, then rows
// matched on other fields (e.g. tags).
const getRelevanceScore = (name, q) => {
  const lower = (name || '').toLowerCase();
  if (lower === q) return 0;
  if (lower.startsWith(q)) return 1;
  if (lower.split(/\s+/).some((word) => word.startsWith(q))) return 2;
  if (lower.includes(q)) return 3;
  return 4;
};

// Tokenized like exerciseSearch: every whitespace-separated term must hit.
export const tokenizedNameSearch = (row, q) => {
  const haystack = (row.name || '').toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

// Muted placeholder for an empty cell — shared by every navigator manage
// table.
const DashText = styled.span`
  color: #CCCCCC;
`;

export const Dash = () => <DashText>–</DashText>;

// Mobile card list + error state (opt-in via renderMobileCard / hasError)

const MobileList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const StateContainer = styled.div`
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #9E9E9E;
  font-size: 14px;
  padding: 40px 16px;
  text-align: center;
`;

const ErrorState = ({ onRetry, message }) => (
  <StateContainer>
    <span>{message}</span>
    <Button
      name='Retry'
      bgColor='#FFF'
      borderColor='#EEE'
      txtColor='#444444'
      customBorderRadius='4px'
      fontSize='14px'
      styledTextProps={{ fontSize: '14px', fontWeight: 700 }}
      onClick={onRetry}
    />
  </StateContainer>
);

ErrorState.propTypes = {
  onRetry: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
};

// Main Component

const NavigatorManageTable = React.forwardRef(({
  data,
  columns,
  isLoading,
  hasError,
  errorMessage,
  onRetry,
  hideSearch,
  searchKeys,
  searchFn,
  searchPlaceholder,
  emptyMessage,
  searchEmptyMessage,
  bulkActions,
  onBulkAction,
  renderBulkBar,
  isRowSelectable,
  entityLabel,
  defaultSortBy,
  bannerContent,
  bannerPosition,
  extraActionContent,
  onFilteredDataChange,
  renderMobileCard,
  clearSelectionToken,
  virtualizeRows,
  variableRowHeight,
  searchQuery: controlledSearchQuery,
  onSearchQueryChange,
  searchControl,
}, ref) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const isSearchControlled = controlledSearchQuery !== undefined;
  const searchQuery = isSearchControlled ? controlledSearchQuery : internalSearchQuery;
  const setSearchQuery = isSearchControlled ? onSearchQueryChange : setInternalSearchQuery;
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkDropdownOpen, setIsBulkDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia('(max-width: 767px)').matches,
  );
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [headerGutter, setHeaderGutter] = useState(0);
  const bulkDropdownRef = useRef(null);
  const scrollerRef = useRef(null);
  const listRef = useRef(null);
  const listOuterRef = useRef(null);
  const rowHeightsRef = useRef(new Map());
  const resetFrameRef = useRef(null);
  const resetIndexRef = useRef(null);
  const listWidthRef = useRef(null);

  const hasCustomBulkBar = typeof renderBulkBar === 'function';
  const hasBulkActions = bulkActions && bulkActions.length > 0;
  const hasSelection = hasBulkActions || hasCustomBulkBar;
  const useMobileCards = typeof renderMobileCard === 'function' && isMobile;
  const useVariableRows = virtualizeRows && variableRowHeight;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // position: sticky can't escape react-window's overflow container, so on the
  // virtualized path sticky cells pin to the right edge via a transform driven
  // by this CSS variable (updated on scroll without re-rendering rows).
  const updateStickyShift = useCallback((el) => {
    if (!el) return;
    el.style.setProperty('--sticky-shift', `${el.scrollLeft - (el.scrollWidth - el.clientWidth)}px`);
  }, []);

  const handleHScroll = useCallback((e) => {
    setIsScrolled(e.currentTarget.scrollLeft > 0);
    if (virtualizeRows) updateStickyShift(e.currentTarget);
  }, [virtualizeRows, updateStickyShift]);

  // Drop selected ids that no longer exist in the data (e.g. after a delete).
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const validIds = new Set(data.map((r) => r.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [data]);

  // External clear (e.g. the survivor should deselect once a merge settles).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [clearSelectionToken]);

  useEffect(() => {
    if (!isBulkDropdownOpen) return undefined;
    const handle = (e) => {
      if (bulkDropdownRef.current && !bulkDropdownRef.current.contains(e.target)) {
        setIsBulkDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isBulkDropdownOpen]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 100);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return data;
    const q = debouncedQuery.trim().toLowerCase();
    const matches = searchFn
      ? data.filter((row) => searchFn(row, q))
      : data.filter((row) => searchKeys.some((key) => {
        const val = row[key];
        return val && String(val).toLowerCase().includes(q);
      }));
    // Rank by match relevance so "goblet squat" beats "dumbbell goblet squat";
    // column sorting is suspended while a query is active (see below).
    const scores = new Map(matches.map((row) => [row, getRelevanceScore(row.name, q)]));
    return matches.sort((a, b) => (
      (scores.get(a) - scores.get(b))
      || (a.name || '').localeCompare(b.name || '')
    ));
  }, [data, debouncedQuery, searchKeys, searchFn]);

  useEffect(() => {
    onFilteredDataChange(filteredData);
  }, [filteredData, onFilteredDataChange]);

  // Select-all only counts selectable rows, so read-only rows are never bulk-selected.
  const selectableIds = useMemo(
    () => (hasSelection ? filteredData.filter((r) => isRowSelectable(r)).map((r) => r.id) : []),
    [hasSelection, filteredData, isRowSelectable],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectableIds.some((id) => selectedIds.has(id));

  const toggleAll = useCallback(() => {
    if (allSelected || someSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  }, [allSelected, someSelected, selectableIds]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const tableData = useMemo(
    () => filteredData.map((row) => {
      const selectable = isRowSelectable(row);
      return {
        ...row,
        isSelected: selectedIds.has(row.id),
        isSelectable: selectable,
        onToggle: hasSelection && selectable ? toggleOne : undefined,
      };
    }),
    [filteredData, selectedIds, toggleOne, hasSelection, isRowSelectable],
  );

  const selectColumn = useMemo(() => ({
    id: 'select',
    disableSortBy: true,
    headerStyle: { width: '40px', padding: '4px 8px 4px 16px' },
    cellStyle: { width: '40px', padding: '6px 2px 6px 16px' },
    Header: '',
    Cell: SelectCell,
  }), []);

  const finalColumns = useMemo(
    () => (hasSelection ? [selectColumn, ...columns] : columns),
    [hasSelection, selectColumn, columns],
  );

  const {
    headerGroups, rows, prepareRow, setSortBy,
  } = useTable(
    {
      columns: finalColumns,
      data: tableData,
      autoResetSortBy: false,
      initialState: { sortBy: defaultSortBy },
      getRowId: (row) => row.id,
    },
    useSortBy,
    useSticky,
  );

  const getItemSize = useCallback(
    (index) => rowHeightsRef.current.get(rows[index].original.id) ?? ROW_HEIGHT,
    [rows],
  );

  const handleMeasure = useCallback((id, height) => {
    if (!useVariableRows) return;
    const cached = rowHeightsRef.current.get(id);
    if (Math.abs((cached ?? ROW_HEIGHT) - height) < 1) return;
    rowHeightsRef.current.set(id, height);
    const rowIndex = rows.findIndex((row) => row.original.id === id);
    if (rowIndex === -1) return;
    resetIndexRef.current = resetIndexRef.current === null
      ? rowIndex
      : Math.min(resetIndexRef.current, rowIndex);
    if (resetFrameRef.current === null) {
      resetFrameRef.current = requestAnimationFrame(() => {
        const minIndex = resetIndexRef.current;
        resetIndexRef.current = null;
        resetFrameRef.current = null;
        if (minIndex !== null) listRef.current?.resetAfterIndex(minIndex, true);
      });
    }
  }, [rows, useVariableRows]);

  const handleListResize = useCallback(({ width }) => {
    if (!useVariableRows || listWidthRef.current === width) return;
    listWidthRef.current = width;
    rowHeightsRef.current.clear();
    listRef.current?.resetAfterIndex(0, true);
  }, [useVariableRows]);

  useEffect(() => {
    if (!useVariableRows) return undefined;
    listRef.current?.resetAfterIndex(0, true);
    return undefined;
  }, [rows, useVariableRows]);

  useEffect(() => {
    listWidthRef.current = null;
    if (useVariableRows) rowHeightsRef.current.clear();
  }, [useVariableRows]);

  useEffect(() => () => {
    if (resetFrameRef.current !== null) {
      cancelAnimationFrame(resetFrameRef.current);
      resetFrameRef.current = null;
    }
  }, []);

  const setListOuterRef = useCallback((el) => {
    listOuterRef.current = el;
    if (!el) return;
    const gutter = el.offsetWidth - el.clientWidth;
    setHeaderGutter(gutter);
  }, []);

  // The header has no scrollbar gutter while the virtual list does.
  useEffect(() => {
    const el = listOuterRef.current;
    if (!virtualizeRows || !el) {
      setHeaderGutter(0);
      return undefined;
    }

    const measureGutter = () => {
      const gutter = el.offsetWidth - el.clientWidth;
      setHeaderGutter((current) => (current === gutter ? current : gutter));
    };

    measureGutter();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureGutter);
    observer.observe(el);
    return () => observer.disconnect();
  }, [rows.length, virtualizeRows]);

  // While a query is active, clear react-table's sort so the relevance order
  // from filteredData shows through; restore the default sort when it clears.
  // A header click during a search still re-sorts, which is intentional.
  const hasActiveQuery = !!debouncedQuery.trim();
  const prevHasQueryRef = useRef(hasActiveQuery);
  useEffect(() => {
    if (prevHasQueryRef.current === hasActiveQuery) return;
    prevHasQueryRef.current = hasActiveQuery;
    setSortBy(hasActiveQuery ? [] : defaultSortBy);
  }, [hasActiveQuery, setSortBy, defaultSortBy]);

  // Imperative scroll-to-row used by the merge affordance: clear an active search
  // first if it's hiding the target, then scroll once the rows re-filter.
  useImperativeHandle(ref, () => ({
    scrollToRow: (id) => setPendingScrollId(id),
  }), []);

  // One filter-clear attempt per scroll target: even with an empty search box,
  // parent-owned filters (e.g. the tag menu) can hide the row, and
  // onSearchQueryChange('') is the signal that resets those too. The latch
  // stops a retry loop when the row genuinely isn't in the data.
  const scrollClearAttemptRef = useRef(null);

  useEffect(() => {
    if (pendingScrollId == null) return undefined;
    const rowIndex = rows.findIndex((r) => r.original.id === pendingScrollId);
    if (rowIndex === -1) {
      if (scrollClearAttemptRef.current !== pendingScrollId) {
        scrollClearAttemptRef.current = pendingScrollId;
        setSearchQuery('');
        return undefined;
      }
      if (debouncedQuery) return undefined;
      scrollClearAttemptRef.current = null;
      setPendingScrollId(null);
      return undefined;
    }
    scrollClearAttemptRef.current = null;
    // Offset highlight to 1.5 rows above target so it doesn't get pinned to the top edge
    if (virtualizeRows) {
      if (listRef.current) {
        if (useVariableRows) listRef.current.scrollToItem(rowIndex, 'center');
        else listRef.current.scrollTo(Math.max(0, (rowIndex * ROW_HEIGHT) - (ROW_HEIGHT * 1.5)));
      }
    } else {
      const container = scrollerRef.current;
      const el = container?.querySelector(`[data-row-id="${pendingScrollId}"]`);
      if (el && container) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = el.getBoundingClientRect();
        const scrollOffset = itemRect.top - containerRect.top + container.scrollTop
          - (ROW_HEIGHT * 1.5);
        container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
      }
    }
    setHighlightId(pendingScrollId);
    setPendingScrollId(null);
    const timer = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(timer);
  }, [pendingScrollId, rows, searchQuery, debouncedQuery, virtualizeRows, useVariableRows]);

  const tableMinWidth = headerGroups[0]?.headers.reduce((sum, col) => {
    const hs = col.headerStyle || {};
    return sum + parseInt(hs.width || hs.minWidth || '0', 10);
  }, 0) || 800;

  // Re-derive the sticky shift when the scrollable width changes (data, columns,
  // viewport) — scroll events alone don't cover those.
  useLayoutEffect(() => {
    if (!virtualizeRows) return undefined;
    const update = () => updateStickyShift(scrollerRef.current);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [virtualizeRows, updateStickyShift, rows.length, tableMinWidth]);

  const entityLabelPlural = `${entityLabel}s`;
  const selectedCount = selectedIds.size;
  const showError = !isLoading && hasError && rows.length === 0;
  const showEmpty = !isLoading && !hasError && rows.length === 0;

  const bulkBar = hasSelection && filteredData.length > 0 && selectedCount > 0 && (
    hasCustomBulkBar
      ? renderBulkBar({
        selectedIds: Array.from(selectedIds),
        selectedCount,
        clearSelection,
        toggleAll,
        allSelected,
      })
      : (
        <BulkActionsBar>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#77A7FF' }}>
            {`${selectedCount} ${selectedCount === 1 ? entityLabel : entityLabelPlural} selected`}
          </Text>
          {bulkActions.length === 1 ? (
            <BulkDropdownButton
              type='button'
              bgColor={bulkActions[0].iconColor}
              hoverColor={bulkActions[0].iconColor === '#FF0505' ? '#CC0404' : undefined}
              onClick={() => onBulkAction(bulkActions[0].key, Array.from(selectedIds))}
            >
              {bulkActions[0].icon && (
                <Icon icon={bulkActions[0].icon} size={14} color='#fff' />
              )}
              {typeof bulkActions[0].label === 'function'
                ? bulkActions[0].label(Array.from(selectedIds))
                : bulkActions[0].label}
            </BulkDropdownButton>
          ) : (
            <BulkDropdownContainer ref={bulkDropdownRef}>
              <BulkDropdownButton
                type='button'
                onClick={() => setIsBulkDropdownOpen((prev) => !prev)}
              >
                Bulk Actions
                <Icon icon='gs-arrow-down' size={10} color='#fff' styledProps={{ marginLeft: '8px' }} />
              </BulkDropdownButton>
              {isBulkDropdownOpen && (
                <BulkDropdownMenu>
                  {bulkActions.map((action) => (
                    <React.Fragment key={action.key}>
                      {action.dividerBefore && <BulkDropdownDivider />}
                      <BulkDropdownItem
                        type='button'
                        onClick={() => {
                          onBulkAction(action.key, Array.from(selectedIds));
                          setIsBulkDropdownOpen(false);
                        }}
                      >
                        {action.icon && (
                          <Icon
                            icon={action.icon}
                            size={14}
                            color={action.iconColor || '#1A1A1A'}
                          />
                        )}
                        {typeof action.label === 'function'
                          ? action.label(Array.from(selectedIds))
                          : action.label}
                      </BulkDropdownItem>
                    </React.Fragment>
                  ))}
                </BulkDropdownMenu>
              )}
            </BulkDropdownContainer>
          )}
        </BulkActionsBar>
      )
  );

  const showSearchRow = !hideSearch || extraActionContent || bulkBar || searchControl
    || (bannerPosition === 'inline' && bannerContent);

  return (
    <>
      {bannerPosition === 'top' && bannerContent && <BannerWrapper>{bannerContent}</BannerWrapper>}
      {showSearchRow && (
        <SearchRow>
          {searchControl ? (
            <SearchControlSlot>{searchControl}</SearchControlSlot>
          ) : (
            !hideSearch && (
              <SearchWrapper>
                <SearchIconWrapper>
                  <Icon icon='magnifying-glass' size={16} color='#929292' />
                </SearchIconWrapper>
                <Input
                  name='manage-table-search'
                  inputOnly
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  styledPropsInput={{
                    width: '100%',
                    height: '40px',
                    fontSize: '14px',
                    paddingLeft: '36px',
                  }}
                  stylePropsWrapper={{ border: '1px solid #D6D6D6', borderRadius: '4px' }}
                />
              </SearchWrapper>
            )
          )}
          {extraActionContent && <ExtraActionSlot>{extraActionContent}</ExtraActionSlot>}
          {bannerPosition === 'inline' && bannerContent && (
            <InlineBannerSlot>{bannerContent}</InlineBannerSlot>
          )}
          {bulkBar && <SearchRowActions>{bulkBar}</SearchRowActions>}
        </SearchRow>
      )}
      {bannerPosition === 'bottom' && bannerContent && <BannerWrapper>{bannerContent}</BannerWrapper>}

      {useMobileCards ? (
        <MobileList ref={scrollerRef}>
          {isLoading && <SpinnerContainer><Spinner /></SpinnerContainer>}
          {showError && <ErrorState onRetry={onRetry} message={errorMessage} />}
          {showEmpty && (
            <StateContainer>
              <span>{searchQuery.trim() ? searchEmptyMessage : emptyMessage}</span>
            </StateContainer>
          )}
          {!isLoading && rows.map((row) => {
            prepareRow(row);
            return (
              <div key={row.id} data-row-id={row.original.id}>
                {renderMobileCard(row.original, {
                  isHighlighted: highlightId != null && row.original.id === highlightId,
                })}
              </div>
            );
          })}
        </MobileList>
      ) : (
        <ScrollContainer>
          <TableContainer virtualizeRows={virtualizeRows} isLoading={isLoading}>
            <HScroller
              virtualizeRows={virtualizeRows}
              isLoading={isLoading}
              ref={scrollerRef}
              onScroll={handleHScroll}
            >
              <HeaderRow
                style={{
                  minWidth: tableMinWidth,
                  paddingRight: virtualizeRows ? headerGutter : undefined,
                }}
              >
                {headerGroups[0].headers.map((column) => {
                  const sortProps = column.getSortByToggleProps({ title: undefined });
                  const headerProps = column.getHeaderProps(sortProps);
                  const isSticky = !!column.sticky;
                  return (
                    <HeaderCell
                      {...headerProps}
                      data-column-id={column.id}
                      canSort={column.canSort}
                      style={{
                        ...headerProps.style,
                        flex: column.headerStyle?.flex,
                        width: column.headerStyle?.flex ? undefined : column.headerStyle?.width,
                        minWidth: column.headerStyle?.minWidth,
                        padding: column.headerStyle?.padding,
                        justifyContent: column.headerStyle?.textAlign === 'center' ? 'center' : undefined,
                        ...(isSticky && {
                          background: '#F2F2F2',
                          boxShadow: isScrolled ? '-1px 0 0 0 #EEEEEE' : 'none',
                        }),
                      }}
                    >
                      {column.id === 'select' ? (
                        <SquareCheckbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={toggleAll}
                          variant='minus'
                        />
                      ) : (
                        <>
                          {column.render('Header')}
                          {column.canSort && <SortIcon column={column} />}
                        </>
                      )}
                    </HeaderCell>
                  );
                })}
              </HeaderRow>
              {rows.length > 0 && (virtualizeRows ? (
                <VirtualBodyFill style={{ minWidth: tableMinWidth }}>
                  <AutoSizer onResize={useVariableRows ? handleListResize : undefined}>
                    {({ height, width }) => (
                      useVariableRows ? (
                        <VariableSizeList
                          ref={listRef}
                          outerRef={setListOuterRef}
                          height={height}
                          width={width}
                          itemCount={rows.length}
                          itemSize={getItemSize}
                          estimatedItemSize={ROW_HEIGHT}
                          itemData={{
                            rows,
                            prepareRow,
                            isScrolled,
                            highlightId,
                            variableRows: true,
                            onMeasure: handleMeasure,
                          }}
                          itemKey={(index, listData) => listData.rows[index].id}
                          style={{ overflowX: 'hidden' }}
                        >
                          {VirtualRowRenderer}
                        </VariableSizeList>
                      ) : (
                        <FixedSizeList
                          ref={listRef}
                          outerRef={setListOuterRef}
                          height={height}
                          width={width}
                          itemCount={rows.length}
                          itemSize={ROW_HEIGHT}
                          itemData={{
                            rows, prepareRow, isScrolled, highlightId,
                          }}
                          itemKey={(index, listData) => listData.rows[index].id}
                          style={{ overflowX: 'hidden' }}
                        >
                          {VirtualRowRenderer}
                        </FixedSizeList>
                      )
                    )}
                  </AutoSizer>
                </VirtualBodyFill>
              ) : (
                <VirtualBody style={{ minWidth: tableMinWidth }}>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      row={row}
                      prepareRow={prepareRow}
                      isScrolled={isScrolled}
                      isHighlighted={highlightId != null && row.original.id === highlightId}
                    />
                  ))}
                </VirtualBody>
              ))}
              {isLoading && rows.length === 0 && <SpinnerContainer><Spinner /></SpinnerContainer>}
              {showError && <ErrorState onRetry={onRetry} message={errorMessage} />}
              {showEmpty && (
              <EmptyStateContainer>
                <Text styledTextProps={{ color: '#9E9E9E', fontSize: '14px' }}>
                  {searchQuery.trim() ? searchEmptyMessage : emptyMessage}
                </Text>
              </EmptyStateContainer>
              )}
            </HScroller>
          </TableContainer>
        </ScrollContainer>
      )}
    </>
  );
});

NavigatorManageTable.displayName = 'NavigatorManageTable';

NavigatorManageTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  onRetry: PropTypes.func,
  hideSearch: PropTypes.bool,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  searchFn: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  searchEmptyMessage: PropTypes.string,
  bulkActions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    dividerBefore: PropTypes.bool,
  })),
  onBulkAction: PropTypes.func,
  renderBulkBar: PropTypes.func,
  isRowSelectable: PropTypes.func,
  entityLabel: PropTypes.string,
  defaultSortBy: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool,
  })),
  bannerContent: PropTypes.node,
  bannerPosition: PropTypes.oneOf(['top', 'bottom', 'inline']),
  extraActionContent: PropTypes.node,
  onFilteredDataChange: PropTypes.func,
  renderMobileCard: PropTypes.func,
  clearSelectionToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  virtualizeRows: PropTypes.bool,
  variableRowHeight: PropTypes.bool,
  searchQuery: PropTypes.string,
  onSearchQueryChange: PropTypes.func,
  searchControl: PropTypes.node,
};

NavigatorManageTable.defaultProps = {
  isLoading: false,
  hasError: false,
  errorMessage: 'Something went wrong.',
  onRetry: () => {},
  hideSearch: false,
  searchKeys: ['first', 'last', 'email'],
  searchFn: null,
  searchPlaceholder: 'Search...',
  emptyMessage: 'No results found.',
  searchEmptyMessage: 'No results match your search.',
  bulkActions: [],
  onBulkAction: () => {},
  renderBulkBar: null,
  isRowSelectable: () => true,
  entityLabel: 'item',
  defaultSortBy: [{ id: 'last', desc: false }],
  bannerContent: null,
  bannerPosition: 'bottom',
  extraActionContent: null,
  onFilteredDataChange: () => {},
  renderMobileCard: null,
  clearSelectionToken: 0,
  virtualizeRows: false,
  variableRowHeight: false,
  searchQuery: undefined,
  onSearchQueryChange: () => {},
  searchControl: null,
};

export default NavigatorManageTable;
